import styled from "styled-components";
import { motion } from "framer-motion";
import { CalendarCheck, Target, Briefcase } from "lucide-react";

/* ================= SECTION ================= */

const Section = styled.section`
  position: relative;
  padding: 180px 20px;
  overflow: hidden;

  background: radial-gradient(
      900px at 18% 0%,
      rgba(245, 158, 11, 0.1),
      transparent 55%
    ),
    radial-gradient(700px at 82% 30%, rgba(56, 189, 248, 0.12), transparent 60%),
    ${({ theme }) => theme.colors.bg};

  &:after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.55));
    pointer-events: none;
  }
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 1200px;
  margin: auto;
  position: relative;
  z-index: 1;
`;

/* ================= HEADER ================= */

const Heading = styled(motion.h2)`
  text-align: center;
  font-size: clamp(36px, 5vw, 60px);
  font-weight: 1000;
  line-height: 1.05;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  text-align: center;
  max-width: 820px;
  margin: 28px auto 0;
  font-size: 19px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= TIMELINE ================= */

const TimelineWrapper = styled.div`
  position: relative;
  margin-top: 120px;
`;

const TimelineConnector = styled.div`
  position: absolute;
  top: 210px;
  left: 0;
  right: 0;
  height: 2px;

  background-image: linear-gradient(
    to right,
    rgba(56, 189, 248, 0.35) 40%,
    transparent 0%
  );
  background-size: 18px 2px;
  background-repeat: repeat-x;

  animation: dash 8s linear infinite;

  @keyframes dash {
    from {
      background-position: 0 0;
    }
    to {
      background-position: 240px 0;
    }
  }

  @media (max-width: 1000px) {
    display: none;
  }
`;

const Timeline = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  position: relative;
  padding: 54px 44px;
  border-radius: 30px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  box-shadow: 0 30px 90px rgba(2, 6, 23, 0.7);
  overflow: hidden;

  ${({ emphasize }) =>
    emphasize &&
    `
      box-shadow: 0 60px 140px rgba(56,189,248,0.28);
      transform: translateY(-10px);
    `}

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      600px at 20% 0%,
      rgba(245, 158, 11, 0.18),
      transparent 60%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover:before {
    opacity: 1;
  }
`;

/* ================= CLOCK ================= */

const Clock = styled(motion.div)`
  position: absolute;
  top: 18px;
  right: 18px;
  font-size: 18px;
  opacity: 0.08;
`;

/* ================= BADGE ================= */

const DayBadge = styled.div`
  display: inline-block;
  margin-bottom: 18px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 800;
  color: #fff;

  background: linear-gradient(90deg, #f59e0b, #38bdf8);
`;

/* ================= ICON ================= */

const IconWrap = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 22px;
  display: grid;
  place-items: center;
  margin-bottom: 28px;
  color: #fff;

  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  box-shadow: 0 0 60px rgba(56, 189, 248, 0.35);
`;

/* ================= TEXT ================= */

const Title = styled.h4`
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const Text = styled.p`
  font-size: 16.5px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= ANIMATIONS ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.22 },
  },
};

/* ================= DATA ================= */

const OUTCOMES = [
  {
    stage: "Stage 1",
    icon: CalendarCheck,
    title: "Clarity instead of chaos",
    text: "Users stop switching directions and finally understand what deserves attention right now.",
  },
  {
    stage: "Stage 2",
    icon: Target,
    title: "Execution with confidence",
    text: "Daily tasks and roadmap structure help users build meaningful work without freezing on what to do next.",
  },
  {
    stage: "Stage 3",
    icon: Briefcase,
    title: "Career-ready, not just hopeful",
    text: "Over time the system creates proof of work, stronger projects, and more confidence for interviews and job applications.",
  },
];

/* ================= COMPONENT ================= */

export default function OutcomesSection() {
  return (
    <Section>
      <Container>
        <Heading
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          What Changes When You Follow the System
        </Heading>

        <Sub
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          This is not overnight success.
          <br />
          It is steady, trackable progress that compounds week after week.
        </Sub>

        <TimelineWrapper>
          <TimelineConnector />

          <Timeline
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-160px" }}
          >
            {OUTCOMES.map((item, i) => {
              const Icon = item.icon;
              return (
                <Card
                  key={i}
                  variants={fadeUp}
                  emphasize={i === 2}
                  whileHover={{ y: -18 }}
                  transition={{ type: "spring", stiffness: 140 }}
                >
                  <Clock
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 40,
                      ease: "linear",
                      repeat: Infinity,
                    }}
                  >
                    ⏱
                  </Clock>

                  <DayBadge>{item.stage}</DayBadge>

                  <IconWrap>
                    <Icon size={30} />
                  </IconWrap>

                  <Title>{item.title}</Title>
                  <Text>{item.text}</Text>
                </Card>
              );
            })}
          </Timeline>
        </TimelineWrapper>
      </Container>
    </Section>
  );
}
